import { format } from 'date-fns';
import moment from 'moment';

// Status enum -> display config. Shared by the on-screen StatusBadge and the CSV export
// so the two never drift apart.
export const STATUS_CONFIG = {
	COMPLETE: { bg: '#e6f9f4', text: '#14A07E', label: 'Complete' },
	READY_TO_PUBLISH: { bg: '#EAF6FC', text: '#2A9AC7', label: 'Ready to Publish' },
	IN_PROGRESS: { bg: '#e8f1fe', text: '#1B78F5', label: 'In Progress' },
	NOT_STARTED: { bg: '#FEF0F0', text: '#D94848', label: 'Not Started' },
};

export function statusLabel(status) {
	return (STATUS_CONFIG[status] || STATUS_CONFIG.NOT_STARTED).label;
}

// Matches the on-screen date rendering (date-fns 'dd/MM/yyyy HH:mm'); blank for missing/invalid.
export function formatExportDate(value) {
	if (!value) return '';
	const d = new Date(value);
	return isNaN(d.getTime()) ? '' : format(d, 'dd/MM/yyyy HH:mm');
}

export function commentaryExportFilename(quarter) {
	const safeQuarter = quarter || 'all';
	return `commentary-tracker_${safeQuarter}_${moment().format('YYYY-MM-DD')}.csv`;
}

// CSV column order + react-csv header mapping (label = column heading, key = row property).
export const EXPORT_HEADERS = [
	{ label: 'Quarter', key: 'quarter' },
	{ label: 'Company', key: 'company' },
	{ label: 'Portfolios Complete', key: 'portfoliosComplete' },
	{ label: 'Portfolios Assigned', key: 'portfoliosAssigned' },
	{ label: 'Company Status', key: 'companyStatus' },
	{ label: 'Fund Number', key: 'fundNumber' },
	{ label: 'Fund Name', key: 'fundName' },
	{ label: 'Portfolio Status', key: 'portfolioStatus' },
	{ label: 'Last Updated By', key: 'lastUpdatedBy' },
	{ label: 'Last Updated Email', key: 'lastUpdatedByEmail' },
	{ label: 'Last Updated At', key: 'lastUpdatedAt' },
	{ label: 'Submitted By', key: 'submittedBy' },
	{ label: 'Submitted Email', key: 'submittedByEmail' },
	{ label: 'Submitted At', key: 'submittedAt' },
];

const EMPTY_PORTFOLIO_FIELDS = {
	fundNumber: '',
	fundName: '',
	portfolioStatus: '',
	lastUpdatedBy: '',
	lastUpdatedByEmail: '',
	lastUpdatedAt: '',
	submittedBy: '',
	submittedByEmail: '',
	submittedAt: '',
};

/**
 * Flatten the filtered/sorted company list into one CSV row per portfolio, preserving the
 * on-screen company order. A company with no portfolios produces a single row with the
 * portfolio columns left blank. Company-level context columns repeat on each of its rows.
 *
 * @param {Array} companies   the already filtered + sorted company rows (as shown on screen)
 * @param {string} quarter    the selected quarter
 * @param {Object} detailsById  map of externalAccessId -> tracker detail ({ portfolios: [...] })
 */
export function buildExportRows(companies, quarter, detailsById) {
	const rows = [];
	(companies || []).forEach(c => {
		const base = {
			quarter: quarter || '',
			company: c?.extCompanyName || '',
			portfoliosComplete: c?.portfoliosComplete ?? '',
			portfoliosAssigned: c?.portfoliosAssigned ?? '',
			companyStatus: statusLabel(c?.status),
		};
		const portfolios = detailsById?.[c?.externalAccessId]?.portfolios || [];
		if (portfolios.length === 0) {
			rows.push({ ...base, ...EMPTY_PORTFOLIO_FIELDS });
		} else {
			portfolios.forEach(p => {
				rows.push({
					...base,
					fundNumber: p?.fundNumber || '',
					fundName: p?.fundName || '',
					portfolioStatus: statusLabel(p?.status),
					lastUpdatedBy: p?.lastUpdatedBy || '',
					lastUpdatedByEmail: p?.lastUpdatedByEmail || '',
					lastUpdatedAt: formatExportDate(p?.lastUpdatedAt),
					submittedBy: p?.submittedBy || '',
					submittedByEmail: p?.submittedByEmail || '',
					submittedAt: formatExportDate(p?.submittedAt),
				});
			});
		}
	});
	return rows;
}
